import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserRecord } from '@cdw-ae/common-data';
import { ICdwSearchGridDataService } from '@cdw-ae/common-ui-components-search-grid';
import {
  IGetSqlDataParams,
  SelectedFilterField,
  SqlResponse,
} from '@cdw-ae/filter-bar-interfaces';
import { USERS_ROUTES } from '@cdw-ae/routes-access-api';
import { getQueryStringFromParams } from '@cdw-ae/routes-shared-api';
import { catchError, Observable, of } from 'rxjs';
import { ApiUrlBuilderService } from '../../../../../../common/services/api-url-builder.service';
import { AccessManagementFrontendAuthService } from '../access-management-frontend-auth.service';

export interface DirectoryUserMatch {
  email: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class UsersDataService implements ICdwSearchGridDataService<UserRecord> {
  constructor(
    private httpClient: HttpClient,
    _accessManagementAuthService: AccessManagementFrontendAuthService,
    private readonly urlBuilder: ApiUrlBuilderService
  ) {}

  public fetchSqlData(
    params: IGetSqlDataParams
  ): Observable<SqlResponse<UserRecord>> {
    const { activeFilters, startRow, endRow }: IGetSqlDataParams = params;
    return this.getUsers(activeFilters, startRow, endRow);
  }

  public getUsers(
    activeFilters?: SelectedFilterField[],
    startRow?: number,
    endRow?: number
  ): Observable<SqlResponse<UserRecord>> {
    const url = this.urlBuilder.backendApi('users', USERS_ROUTES.ROOT);
    const queryString = getQueryStringFromParams({
      activeFilters,
      startRow,
      endRow,
    });
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return this.httpClient
      .get<SqlResponse<UserRecord>>(fullUrl)
      .pipe(
        catchError(() =>
          of({ data: [], itemTotal: 0 } as SqlResponse<UserRecord>)
        )
      );
  }

  public searchUsersInDirectory(
    params:
      | { searchBy: 'email'; searchValue: string }
      | {
          searchBy: 'name';
          givenName?: string;
          familyName?: string;
          limit?: number;
        }
  ): Observable<{ users: DirectoryUserMatch[] }> {
    const url = this.urlBuilder.backendApi('authentication', 'user-lookup');
    const query = new URLSearchParams({ searchBy: params.searchBy });
    if (params.searchBy === 'email') {
      query.set('searchValue', params.searchValue);
    } else {
      if (params.givenName) query.set('givenName', params.givenName);
      if (params.familyName) query.set('familyName', params.familyName);
      if (params.limit !== undefined) query.set('limit', String(params.limit));
    }
    return this.httpClient.get<{ users: DirectoryUserMatch[] }>(
      `${url}?${query.toString()}`
    );
  }
}
